/**
 * Offerta di un fornitore: inserimento voce per voce e normalizzazione.
 *
 * Una riga per ogni voce del capitolato, con il campo giusto per il tipo di risposta (il server
 * sa in che colonna va il valore: qui si manda solo `valore`). Il **valore normalizzato** è quello
 * che userà la comparazione, se diverso da quello scritto dal fornitore (es. un canone trimestrale
 * riportato a mensile): va motivato nella nota, perché è il punto di controllo della qualità.
 *
 * Un rilancio del fornitore non si scrive sopra: è una revisione nuova, con le voci copiate, e la
 * precedente resta leggibile come «Superata».
 */

import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@shared/context/AuthContext";
import { useToast } from "@shared/components/ui/Toast";
import { gareApi } from "../services/acquistiApi";
import Documenti from "../components/Documenti";
import {
  Campo,
  Numero,
  Data,
  AreaTesto,
  Scelta,
  Caricamento,
  Avviso,
} from "../components/campi";
import {
  SEZIONE_VOCE,
  TIPO_RISPOSTA,
  FORMULA,
  CONFORMITA,
  STATO_OFFERTA,
  STATO_OFFERTA_BADGE,
  OFFERTA_IN_COMPILAZIONE,
  OFFERTA_COMPLETA,
  OFFERTA_RITIRATA,
  RISPOSTA_SI_NO,
  RISPOSTA_TESTO,
  RISPOSTA_DATA,
  FORMULA_MANUALE,
  opzioni,
} from "../constants/optionSet";
import { dataInput, etichetta } from "../utils/formato";

const CAMPI_VOCE = [
  "valore",
  "valoreNormalizzato",
  "noteNormalizzazione",
  "conformita",
  "punteggioManuale",
  "motivazionePunteggio",
];

function inizialeVoce(v) {
  const r = { id: v.id };
  for (const k of CAMPI_VOCE) r[k] = v[k] ?? "";
  return r;
}

function ValoreVoce({ voce, valore, onChange, disabled }) {
  if (voce.tipoRisposta === RISPOSTA_SI_NO) {
    return (
      <select
        className="input"
        value={valore ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        <option value="">—</option>
        <option value="si">Sì</option>
        <option value="no">No</option>
      </select>
    );
  }
  if (voce.tipoRisposta === RISPOSTA_TESTO) {
    return (
      <AreaTesto
        valore={valore}
        onChange={onChange}
        righe={2}
        disabled={disabled}
      />
    );
  }
  if (voce.tipoRisposta === RISPOSTA_DATA) {
    return (
      <Data
        valore={dataInput(valore)}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }
  return (
    <Numero
      valore={valore}
      onChange={onChange}
      step="any"
      disabled={disabled}
    />
  );
}

export default function OffertaPage() {
  const { id, offertaId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { canPerformAction } = useAuth();
  const puoScrivere = canPerformAction("gare.gestisci");

  const [offerta, setOfferta] = useState(null);
  const [testata, setTestata] = useState({});
  const [voci, setVoci] = useState({});
  const [sporche, setSporche] = useState(new Set());
  const [caricamento, setCaricamento] = useState(true);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState(null);

  const carica = useCallback(async () => {
    setCaricamento(true);
    try {
      const o = await gareApi.offerta(id, offertaId);
      setOfferta(o);
      setTestata({
        dataRicezione: dataInput(o.dataRicezione),
        importoTotale: o.importoTotale ?? "",
        validitaGiorni: o.validitaGiorni ?? "",
        noteNormalizzazione: o.noteNormalizzazione ?? "",
        note: o.note ?? "",
        stato: o.stato,
      });
      setVoci(Object.fromEntries(o.voci.map((v) => [v.id, inizialeVoce(v)])));
      setSporche(new Set());
      setErrore(null);
    } catch (e) {
      setErrore(e.message);
    } finally {
      setCaricamento(false);
    }
  }, [id, offertaId]);

  useEffect(() => {
    carica();
  }, [carica]);

  if (caricamento) return <Caricamento testo="Caricamento offerta…" />;
  if (!offerta)
    return <Avviso tipo="errore">{errore || "Offerta non trovata"}</Avviso>;

  const editabile = puoScrivere && offerta.modificabile;

  const setVoce = (voceId, campo) => (valore) => {
    setVoci((m) => ({ ...m, [voceId]: { ...m[voceId], [campo]: valore } }));
    setSporche((s) => new Set(s).add(voceId));
  };

  const azione = async (fn, messaggio) => {
    setInCorso(true);
    setErrore(null);
    try {
      const res = await fn();
      if (messaggio) showToast(messaggio, "success");
      return res;
    } catch (e) {
      setErrore(e.data?.message || e.message || "Operazione fallita");
      return null;
    } finally {
      setInCorso(false);
    }
  };

  const salva = async (e) => {
    e?.preventDefault();
    const okTestata = await azione(() =>
      gareApi.aggiornaOfferta(id, offertaId, {
        ...testata,
        stato: Number(testata.stato),
      }),
    );
    if (!okTestata) return;
    if (sporche.size) {
      const payload = [...sporche].map((vid) => voci[vid]);
      const ok = await azione(() => gareApi.salvaVoci(id, offertaId, payload));
      if (!ok) return;
    }
    showToast("Offerta salvata", "success");
    carica();
  };

  const rilancio = async () => {
    if (
      !window.confirm(
        "Il fornitore ha mandato un'offerta migliorata?\n\nSi crea la revisione successiva con le voci copiate da questa, che diventa «Superata».",
      )
    )
      return;
    const res = await azione(
      () => gareApi.rilancio(id, offertaId),
      "Revisione creata",
    );
    if (res) navigate(`/gare/${id}/offerte/${res.id}`);
  };

  // Voci raggruppate per sezione, nell'ordine del capitolato.
  const gruppi = [];
  for (const v of offerta.voci) {
    const titolo = etichetta(SEZIONE_VOCE, v.sezione, "Senza sezione");
    let g = gruppi[gruppi.length - 1];
    if (!g || g.titolo !== titolo) gruppi.push((g = { titolo, voci: [] }));
    g.voci.push(v);
  }

  return (
    <form onSubmit={salva} className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to={`/gare/${id}`}
          className="text-sm text-acquisti-700 hover:underline"
        >
          ← {offerta.gara.codice} {offerta.gara.nome}
        </Link>
        <h1 className="text-xl font-semibold text-gray-900">
          {offerta.fornitore?.nome} — rev. {offerta.revisione}
        </h1>
        <span className={STATO_OFFERTA_BADGE[offerta.stato] || "badge-gray"}>
          {offerta.statoLabel || etichetta(STATO_OFFERTA, offerta.stato)}
        </span>
        <span className="text-sm text-gray-500">
          {offerta.riepilogo.compilate}/{offerta.riepilogo.voci} voci compilate
        </span>
        {editabile && (
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              className="btn-secondary"
              disabled={inCorso}
              onClick={rilancio}
            >
              Rilancio del fornitore
            </button>
            <button type="submit" className="btn-primary" disabled={inCorso}>
              {inCorso ? "Salvataggio…" : "Salva"}
            </button>
          </div>
        )}
      </div>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!offerta.modificabile && (
        <Avviso tipo="info">
          Questa revisione non è più modificabile
          {offerta.stato !== OFFERTA_IN_COMPILAZIONE &&
          offerta.stato !== OFFERTA_COMPLETA
            ? " (superata da una revisione successiva o ritirata)"
            : " (la gara non accetta più modifiche alle offerte)"}
          .
        </Avviso>
      )}

      <fieldset
        disabled={!editabile}
        className="card grid grid-cols-1 md:grid-cols-4 gap-4"
      >
        <Campo label="Ricevuta il">
          <Data
            valore={testata.dataRicezione}
            onChange={(v) => setTestata((t) => ({ ...t, dataRicezione: v }))}
          />
        </Campo>
        <Campo
          label="Importo totale (IVA escl.)"
          aiuto="Informativo: la comparazione usa le voci."
        >
          <Numero
            valore={testata.importoTotale}
            onChange={(v) => setTestata((t) => ({ ...t, importoTotale: v }))}
            step="0.01"
          />
        </Campo>
        <Campo label="Validità (giorni)">
          <Numero
            valore={testata.validitaGiorni}
            onChange={(v) => setTestata((t) => ({ ...t, validitaGiorni: v }))}
          />
        </Campo>
        <Campo label="Stato">
          <Scelta
            valore={testata.stato}
            onChange={(v) => setTestata((t) => ({ ...t, stato: v }))}
            opzioni={opzioni(STATO_OFFERTA).filter((o) =>
              [
                OFFERTA_IN_COMPILAZIONE,
                OFFERTA_COMPLETA,
                OFFERTA_RITIRATA,
              ].includes(o.value),
            )}
          />
        </Campo>
        <Campo
          label="Note di normalizzazione"
          aiuto="Come l'offerta è stata riportata a base comparabile."
          className="md:col-span-2"
        >
          <AreaTesto
            valore={testata.noteNormalizzazione}
            onChange={(v) =>
              setTestata((t) => ({ ...t, noteNormalizzazione: v }))
            }
          />
        </Campo>
        <Campo label="Note interne" className="md:col-span-2">
          <AreaTesto
            valore={testata.note}
            onChange={(v) => setTestata((t) => ({ ...t, note: v }))}
          />
        </Campo>
      </fieldset>

      {gruppi.map((g) => (
        <section key={g.titolo} className="card !p-0 overflow-x-auto">
          <h2 className="font-semibold text-gray-900 p-4 pb-2">{g.titolo}</h2>
          <table className="table">
            <thead>
              <tr>
                <th className="w-1/3">Voce</th>
                <th>Risposta del fornitore</th>
                <th>Normalizzazione</th>
                <th>Valutazione</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 align-top">
              {g.voci.map((v) => {
                const f = voci[v.id] || {};
                const numerica = ![
                  RISPOSTA_SI_NO,
                  RISPOSTA_TESTO,
                  RISPOSTA_DATA,
                ].includes(v.tipoRisposta);
                const manuale = v.formula === FORMULA_MANUALE;
                return (
                  <tr key={v.id}>
                    <td>
                      <div className="font-medium">
                        {v.nome}
                        {v.obbligatorio && (
                          <span className="badge-danger ml-2">
                            obbligatorio
                          </span>
                        )}
                      </div>
                      {v.descrizione && (
                        <div className="text-xs text-gray-500 mt-1">
                          {v.descrizione}
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mt-1">
                        {etichetta(TIPO_RISPOSTA, v.tipoRisposta)}
                        {v.unitaMisura ? ` · ${v.unitaMisura}` : ""} · peso{" "}
                        {v.peso ?? 0} · {etichetta(FORMULA, v.formula)}
                        {v.sogliaMinima != null && ` · min ${v.sogliaMinima}`}
                        {v.sogliaMassima != null && ` · max ${v.sogliaMassima}`}
                      </div>
                    </td>
                    <td className="min-w-[12rem]">
                      <ValoreVoce
                        voce={v}
                        valore={f.valore}
                        onChange={setVoce(v.id, "valore")}
                        disabled={!editabile}
                      />
                    </td>
                    <td className="min-w-[12rem] space-y-1">
                      {numerica && (
                        <Numero
                          valore={f.valoreNormalizzato}
                          onChange={setVoce(v.id, "valoreNormalizzato")}
                          step="any"
                          placeholder="uguale al valore"
                          disabled={!editabile}
                        />
                      )}
                      <AreaTesto
                        valore={f.noteNormalizzazione}
                        onChange={setVoce(v.id, "noteNormalizzazione")}
                        righe={1}
                        placeholder="nota"
                        disabled={!editabile}
                      />
                    </td>
                    <td className="min-w-[12rem] space-y-1">
                      <Scelta
                        valore={f.conformita}
                        onChange={setVoce(v.id, "conformita")}
                        opzioni={opzioni(CONFORMITA)}
                        vuoto="conformità: decidono le soglie"
                        disabled={!editabile}
                      />
                      {manuale && (
                        <>
                          <Numero
                            valore={f.punteggioManuale}
                            onChange={setVoce(v.id, "punteggioManuale")}
                            min={0}
                            max={v.punteggioMassimo ?? 10}
                            step="0.5"
                            placeholder={`punteggio 0–${v.punteggioMassimo ?? 10}`}
                            disabled={!editabile}
                          />
                          <AreaTesto
                            valore={f.motivazionePunteggio}
                            onChange={setVoce(v.id, "motivazionePunteggio")}
                            righe={1}
                            placeholder="motivazione (obbligatoria)"
                            disabled={!editabile}
                          />
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      ))}

      <Documenti
        base={gareApi.documentiOfferta(id, offertaId)}
        puoScrivere={editabile}
        titolo="Documenti dell'offerta"
      />

      <section className="card text-sm text-gray-600">
        Inserita da <strong>{offerta.creataDa || "—"}</strong>
        {offerta.modificataDa && (
          <>
            {" "}
            · ultima modifica <strong>{offerta.modificataDa}</strong>
          </>
        )}
      </section>
    </form>
  );
}
